import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

const SWIPER_VERSION = '11';
const SWIPER_JS_URL = `https://cdn.jsdelivr.net/npm/swiper@${SWIPER_VERSION}/swiper-bundle.min.mjs`;
const SWIPER_CSS_URL = `https://cdn.jsdelivr.net/npm/swiper@${SWIPER_VERSION}/swiper.min.css`;


const NO_SWIPING_SELECTOR =
  '.card-gallery:has(slideshow-component:not([disabled="true"]) slideshow-slide:not([hidden]) ~ slideshow-slide:not([hidden]))';

let swiperPromise = null;

function ensureSwiperStyles() {
  if (document.querySelector('link[data-swiper-styles]')) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = SWIPER_CSS_URL;
  link.dataset.swiperStyles = '';
  document.head.appendChild(link);
}

function loadSwiper() {
  ensureSwiperStyles();
  swiperPromise ??= import(/* @vite-ignore */ SWIPER_JS_URL).then((module) => module.default);
  return swiperPromise;
}

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Swiper instances keyed by viewport element
   * @type {Map<Element, any>}
   */
  #swipers = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#initCarousels();

    if (this.dataset.recommendationsPerformed === 'true') {
      return;
    }

    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#destroyCarousels();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          this.#initCarousels();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }

  async #initCarousels() {
    const carousels = [...this.querySelectorAll('[data-product-recommendations-carousel]')];
    if (!carousels.length) return;

    carousels.forEach((carousel) => {
      carousel.dataset.carouselReady = 'false';
    });

    try {
      const Swiper = await loadSwiper();
      if (!this.isConnected) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      carousels.forEach((carousel) => {
        const viewport = carousel.querySelector('.swiper');
        const track = carousel.querySelector('.swiper-wrapper');
        if (!viewport || !track || this.#swipers.has(viewport)) return;

        /** @param {boolean} moving */
        const setMoving = (moving) => viewport.toggleAttribute('data-carousel-moving', moving);

        const swiper = new Swiper(viewport, {
          slidesPerView: 'auto',
          spaceBetween: 14,
          noSwipingSelector: NO_SWIPING_SELECTOR,
          watchSlidesProgress: true,
          grabCursor: true,
          watchOverflow: true,
          roundLengths: true,
          threshold: 10,
          mousewheel: { forceToAxis: true },
          speed: prefersReducedMotion ? 0 : 400,
          a11y: { enabled: true },
          keyboard: { enabled: true, onlyInViewport: true },
          on: {
            sliderFirstMove: () => setMoving(true),
            transitionStart: () => setMoving(true),
            touchEnd: () => setMoving(false),
            transitionEnd: () => setMoving(false),
          },
        });

        this.#swipers.set(viewport, swiper);
        carousel.dataset.carouselReady = 'true';
      });
    } catch (error) {
      carousels.forEach((carousel) => {
        carousel.dataset.carouselReady = 'true';
      });
      console.warn('[product-recommendations] Swiper failed to load; using scroll fallback.', error);
    }
  }

  #destroyCarousels() {
    this.#swipers.forEach((swiper) => swiper.destroy(true, true));
    this.#swipers.clear();
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
